/**
 * The colour scheme (auto / light / dark) as a per-user setting (UX-930), stored in the user's UX
 * settings document (API-617) under `app.colorScheme`, so the choice follows the user to another
 * browser. The stored form is the same string the device-level key holds.
 *
 * Two copies, on purpose:
 *
 * - THE SERVER VALUE is the truth once a user is signed in and their document has loaded.
 * - THE DEVICE-LEVEL KEY (`color-scheme-preference`, see `color-scheme-preference.ts`) paints
 *   before React mounts and on the login page, and is rewritten to match the server value when
 *   that arrives. It is never removed.
 *
 * `components/color-scheme-user-setting-sync.tsx` is what joins the two.
 */
import type { UserSettingDefinition } from '../types/user-settings';
import Api from './Api';
import {
    COLOR_SCHEME_PREFERENCE_STORAGE_KEY,
    isColorSchemePreference,
    type ColorSchemePreference
} from './color-scheme-preference';

/** A stored value read safely: anything missing or malformed is `auto`. Never throws. */
export function parseColorSchemePreference(stored: unknown): ColorSchemePreference {
    return isColorSchemePreference(stored) ? stored : 'auto';
}

/**
 * The device-level key, offered to the store's one-time carry-over: when the server has no value
 * for this user and this browser has one, it is sent up.
 *
 * NOT UNDER LOGIN AS. The carry-over needs no click, so without this an admin with a forced scheme
 * would stamp it on every customer they logged in as, and the customer would find their app dark
 * with nobody having chosen that. An admin who CHANGES the scheme while logged in as a customer
 * does change it for that customer; that is the ruling, and it goes through `setValue`, not here.
 */
function readDeviceColorSchemeForCarryOver(): ColorSchemePreference | undefined {
    if (Api.user?.superUserId) return undefined;
    const stored = window.localStorage.getItem(COLOR_SCHEME_PREFERENCE_STORAGE_KEY);
    return isColorSchemePreference(stored) ? stored : undefined;
}

/**
 * The setting. Its value is the user's stored choice, or `undefined` when they have none: the one
 * case the caller must tell apart, because then the device-level key keeps driving the scheme
 * (and is itself `auto` when absent). A value that is stored but malformed reads as `auto`.
 */
export const COLOR_SCHEME_SETTING: UserSettingDefinition<ColorSchemePreference | undefined> = {
    path: ['app', 'colorScheme'],
    parse: stored => (stored === undefined ? undefined : parseColorSchemePreference(stored)),
    legacy: {
        read: readDeviceColorSchemeForCarryOver,
        // The device-level key is kept for good: it is the pre-paint and login-page copy.
        remove: () => undefined
    }
};
